import { rasterAssetDataUrl } from "@/document/assets/rasterAssets";
import type { SceneNode } from "@/document/model/nodes";
import { normalizeImagePaint, type ImagePaint } from "@/document/model/styles";
import { imageRectForPaint } from "@/document/operations/imagePaintGeometry";
import type { Rect } from "@/lib/math/rect";

export interface PencilTraceEdgeMap {
  imageRect: Rect;
  clipRect: Rect;
  points: Array<{ x: number; y: number; strength: number }>;
  grid: Map<string, number[]>;
  cellSize: number;
}

export interface PencilTraceSession {
  id: number;
  nodeId: string;
  ready: Promise<PencilTraceEdgeMap | undefined>;
  edgeMap?: PencilTraceEdgeMap;
}

const maxTraceDimension = 768;
const traceCellSize = 8;
const softThreshold = 24;
const mediumThreshold = 48;
const strongThreshold = 92;
let nextTraceSessionId = 1;
const edgeMapCache = new Map<string, Promise<PencilTraceEdgeMap | undefined>>();

export function imageFillForPencilTrace(node: SceneNode | undefined): ImagePaint | undefined {
  const fill = node?.style.fills.find(paint => paint.type === "image");
  return fill?.type === "image" ? normalizeImagePaint(fill) : undefined;
}

export function createPencilTraceSession(node: SceneNode, paint: ImagePaint): PencilTraceSession | undefined {
  const source = rasterAssetDataUrl(paint.assetId, paint.source);
  if (!source) {
    return undefined;
  }

  const normalized = normalizeImagePaint(paint);
  const imageRect = imageRectForPaint(node.bounds, normalized);
  const cacheKey = `${normalized.assetId ?? source}:${normalized.intrinsicWidth}x${normalized.intrinsicHeight}`;
  let ready = edgeMapCache.get(cacheKey);
  if (!ready) {
    ready = buildPencilTraceEdgeMap(source, imageRect, node.bounds);
    edgeMapCache.set(cacheKey, ready);
  } else {
    ready = ready.then(edgeMap => edgeMap ? edgeMapWithGeometry(edgeMap, imageRect, node.bounds) : undefined);
  }

  return {
    id: nextTraceSessionId++,
    nodeId: node.id,
    ready,
  };
}

export function snapPointToPencilTrace(edgeMap: PencilTraceEdgeMap | undefined, point: { x: number; y: number }, zoom: number): { x: number; y: number } | undefined {
  if (!edgeMap || edgeMap.points.length === 0 || !pointInRect(point, edgeMap.imageRect) || !pointInRect(point, edgeMap.clipRect)) {
    return undefined;
  }

  const radius = Math.max(1.5, 10 / Math.max(0.01, zoom));
  const radiusSquared = radius * radius;
  const minCellX = Math.floor((point.x - radius) / edgeMap.cellSize);
  const maxCellX = Math.floor((point.x + radius) / edgeMap.cellSize);
  const minCellY = Math.floor((point.y - radius) / edgeMap.cellSize);
  const maxCellY = Math.floor((point.y + radius) / edgeMap.cellSize);
  let best: { point: { x: number; y: number }; score: number } | undefined;

  for (let cellY = minCellY; cellY <= maxCellY; cellY += 1) {
    for (let cellX = minCellX; cellX <= maxCellX; cellX += 1) {
      const indexes = edgeMap.grid.get(gridKey(cellX, cellY));
      if (!indexes) {
        continue;
      }

      for (const index of indexes) {
        const candidate = edgeMap.points[index]!;
        const dx = candidate.x - point.x;
        const dy = candidate.y - point.y;
        const distanceSquared = dx * dx + dy * dy;
        if (distanceSquared > radiusSquared) {
          continue;
        }

        const score = distanceSquared / Math.max(1, candidate.strength);
        if (!best || score < best.score) {
          best = { point: candidate, score };
        }
      }
    }
  }

  return best?.point;
}

async function buildPencilTraceEdgeMap(source: string, imageRect: Rect, clipRect: Rect): Promise<PencilTraceEdgeMap | undefined> {
  const image = await loadImage(source);
  if (!image) {
    return undefined;
  }

  const scale = Math.min(1, maxTraceDimension / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    return undefined;
  }

  context.drawImage(image, 0, 0, width, height);
  const data = context.getImageData(0, 0, width, height).data;
  const grayscale = new Float32Array(width * height);
  for (let index = 0; index < width * height; index += 1) {
    const offset = index * 4;
    grayscale[index] = data[offset]! * 0.299 + data[offset + 1]! * 0.587 + data[offset + 2]! * 0.114;
  }

  const magnitude = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const gx =
        -grayscale[(y - 1) * width + x - 1]! + grayscale[(y - 1) * width + x + 1]!
        - 2 * grayscale[y * width + x - 1]! + 2 * grayscale[y * width + x + 1]!
        - grayscale[(y + 1) * width + x - 1]! + grayscale[(y + 1) * width + x + 1]!;
      const gy =
        -grayscale[(y - 1) * width + x - 1]! - 2 * grayscale[(y - 1) * width + x]! - grayscale[(y - 1) * width + x + 1]!
        + grayscale[(y + 1) * width + x - 1]! + 2 * grayscale[(y + 1) * width + x]! + grayscale[(y + 1) * width + x + 1]!;
      magnitude[y * width + x] = Math.hypot(gx, gy) / 4;
    }
  }

  return edgeMapWithGeometry(edgeMapFromMagnitude(magnitude, width, height, imageRect, clipRect), imageRect, clipRect);
}

function edgeMapFromMagnitude(magnitude: Float32Array, width: number, height: number, imageRect: Rect, clipRect: Rect): PencilTraceEdgeMap {
  const points: PencilTraceEdgeMap["points"] = [];
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const strength = magnitude[y * width + x]!;
      if (!isTraceEdgePixel(magnitude, width, x, y, strength)) {
        continue;
      }

      points.push({
        x: imageRect.x + ((x + 0.5) / width) * imageRect.width,
        y: imageRect.y + ((y + 0.5) / height) * imageRect.height,
        strength,
      });
    }
  }

  return indexEdgePoints({ imageRect, clipRect, points, grid: new Map(), cellSize: traceCellSize });
}

function edgeMapWithGeometry(edgeMap: PencilTraceEdgeMap, imageRect: Rect, clipRect: Rect): PencilTraceEdgeMap {
  if (edgeMap.imageRect === imageRect && edgeMap.clipRect === clipRect) {
    return edgeMap;
  }

  return indexEdgePoints({
    imageRect,
    clipRect,
    points: edgeMap.points.map(point => ({
      x: imageRect.x + ((point.x - edgeMap.imageRect.x) / edgeMap.imageRect.width) * imageRect.width,
      y: imageRect.y + ((point.y - edgeMap.imageRect.y) / edgeMap.imageRect.height) * imageRect.height,
      strength: point.strength,
    })),
    grid: new Map(),
    cellSize: edgeMap.cellSize,
  });
}

function indexEdgePoints(edgeMap: PencilTraceEdgeMap): PencilTraceEdgeMap {
  const grid = new Map<string, number[]>();
  edgeMap.points.forEach((point, index) => {
    const key = gridKey(Math.floor(point.x / edgeMap.cellSize), Math.floor(point.y / edgeMap.cellSize));
    const indexes = grid.get(key);
    if (indexes) {
      indexes.push(index);
    } else {
      grid.set(key, [index]);
    }
  });
  return { ...edgeMap, grid };
}

function isTraceEdgePixel(magnitude: Float32Array, width: number, x: number, y: number, strength: number): boolean {
  if (strength >= strongThreshold) {
    return true;
  }

  const threshold = strength >= mediumThreshold ? mediumThreshold : softThreshold;
  if (strength < threshold || !isLocalMaximum(magnitude, width, x, y, strength)) {
    return false;
  }

  let neighborCount = 0;
  for (let oy = -1; oy <= 1; oy += 1) {
    for (let ox = -1; ox <= 1; ox += 1) {
      if ((ox !== 0 || oy !== 0) && magnitude[(y + oy) * width + x + ox]! >= threshold) {
        neighborCount += 1;
      }
    }
  }

  return strength >= mediumThreshold ? neighborCount >= 1 : neighborCount >= 3;
}

function isLocalMaximum(magnitude: Float32Array, width: number, x: number, y: number, strength: number): boolean {
  for (let oy = -1; oy <= 1; oy += 1) {
    for (let ox = -1; ox <= 1; ox += 1) {
      if ((ox !== 0 || oy !== 0) && magnitude[(y + oy) * width + x + ox]! > strength) {
        return false;
      }
    }
  }
  return true;
}

function loadImage(source: string): Promise<HTMLImageElement | undefined> {
  return new Promise(resolve => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(undefined);
    image.src = source;
    if (image.complete && image.naturalWidth > 0) {
      resolve(image);
    }
  });
}

function gridKey(x: number, y: number): string {
  return `${x}:${y}`;
}

function pointInRect(point: { x: number; y: number }, rect: Rect): boolean {
  const left = Math.min(rect.x, rect.x + rect.width);
  const right = Math.max(rect.x, rect.x + rect.width);
  const top = Math.min(rect.y, rect.y + rect.height);
  const bottom = Math.max(rect.y, rect.y + rect.height);
  return point.x >= left && point.x <= right && point.y >= top && point.y <= bottom;
}
